import { useEffect, useState } from 'react';
import { RefreshCw, AlertTriangle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDataFreshnessStore, type DataDomain } from '../lib/stores';
import { useAuthStore } from '../lib/authStore';

export interface StaleInfo {
  /** Something changed that this view should refresh. */
  stale: boolean;
  /** The exact id this view is showing changed (vs a coarse/domain-only signal). */
  precise: boolean;
  /** The relevant change was made by the CURRENT user (suppress the alarm; self-refetch). */
  bySelf: boolean;
  /** Acting user id of the relevant change (null = system/sync/unattributed). */
  byActor: string | null;
  /** The relevant change to THIS id was a deletion. */
  deleted: boolean;
}

const NOT_STALE: StaleInfo = { stale: false, precise: false, bySelf: false, byActor: null, deleted: false };

/**
 * Whether `domain`/`id` data changed since this page last loaded it, and by whom.
 * Pass the detail query's `dataUpdatedAt` — any (re)fetch, including the user's own
 * Refresh, re-baselines, so the result only reflects LATER changes. Pass the entity
 * `id` for id-precise behaviour (a detail page for item X ignores sibling changes);
 * an id-less coarse change (large sync batch / reconnect) still marks it stale.
 */
export function useStaleData(domain: DataDomain, dataUpdatedAt: number, id?: string | number): StaleInfo {
  const coarseVersion = useDataFreshnessStore((s) => s.versions[domain]);
  const coarseActor = useDataFreshnessStore((s) => s.lastActorByDomain[domain] ?? null);
  const key = id != null ? `${domain}:${String(id)}` : null;
  const entity = useDataFreshnessStore((s) => (key ? s.entities[key] : undefined));

  // Baseline = the freshness known at the last (re)fetch. Held in STATE (not a ref)
  // so resetting it after a refetch RE-RENDERS and the returned staleness genuinely
  // toggles back to false. With a ref, `stale` stayed true in the last committed
  // render, so a SECOND consecutive change to the same item produced no false→true
  // edge and effects gated on `stale` (e.g. the self-refetch) never re-fired.
  const [seen, setSeen] = useState(() => ({ cursor: entity?.cursor ?? 0, version: coarseVersion }));
  useEffect(() => {
    const st = useDataFreshnessStore.getState();
    setSeen({ cursor: (key ? st.entities[key]?.cursor : 0) ?? 0, version: st.versions[domain] });
  }, [dataUpdatedAt, domain, key]);

  const me = useAuthStore.getState().user?.id ?? null;
  const isSelf = (actor: string | null) => actor != null && me != null && String(actor) === String(me);

  // Precise: this exact id advanced past what we loaded.
  if (entity && entity.cursor > seen.cursor) {
    return { stale: true, precise: true, bySelf: isSelf(entity.actorUserId), byActor: entity.actorUserId, deleted: entity.change === 'deleted' };
  }
  // Coarse: an id-less change to the domain (big sync / reconnect) — can't prove it
  // was this id, but it might have been, so flag it.
  if (coarseVersion > seen.version) {
    return { stale: true, precise: false, bySelf: isSelf(coarseActor), byActor: coarseActor, deleted: false };
  }
  return NOT_STALE;
}

const bannerBase: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
  padding: 'var(--space-2) var(--space-3)', marginBottom: 'var(--space-3)',
  borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
};

/**
 * A subtle, non-intrusive strip shown on a detail page when its data changed since
 * the page was loaded. The user clicks the action to apply the new data — nothing
 * is yanked out from under them until they do. `variant='deleted'` warns that the
 * item is gone (the caller's `onRefresh` should navigate back).
 */
export function StaleDataBanner(
  { show, onRefresh, variant = 'updated', message }: {
    show: boolean;
    onRefresh: () => void;
    variant?: 'updated' | 'deleted';
    message?: string;
  },
) {
  if (!show) return null;
  const deleted = variant === 'deleted';
  const text = message ?? (deleted ? 'This item was deleted elsewhere.' : 'New data is available for this item.');
  return (
    <div
      role="status"
      style={{
        ...bannerBase,
        background: deleted ? 'var(--accent-error-soft)' : 'var(--accent-primary-soft)',
        color: deleted ? 'var(--accent-error)' : 'var(--accent-primary)',
        border: `1px solid ${deleted ? 'var(--accent-error)' : 'var(--accent-primary)'}`,
      }}
    >
      <RefreshCw size={15} />
      <span style={{ flex: 1 }}>{text}</span>
      <button type="button" className="btn btn-primary btn-sm" onClick={onRefresh}>
        {deleted ? 'Back to list' : 'Refresh'}
      </button>
    </div>
  );
}

/**
 * Shown on an EDIT surface (a form with unsaved changes) when the item being edited
 * was changed by SOMEONE ELSE while editing — saving now could overwrite their work.
 * Warning-tinted, with a clear escape ("Reload latest, discard my edits") and a way
 * to dismiss and keep going ("Keep editing"). `coarse` softens the wording when we
 * can't prove it was this exact item (reconnect / unattributed write).
 */
export function EditConflictBanner(
  { show, actorName, coarse = false, deleted = false, onReload, onKeep, reloadLabel = 'Reload latest', message }: {
    show: boolean;
    actorName?: string | null;
    coarse?: boolean;
    deleted?: boolean;
    onReload: () => void;
    onKeep: () => void;
    /** Primary action label. Compose surfaces (where reload must NOT discard the
     *  typed draft) override this, e.g. "Refresh context". */
    reloadLabel?: string;
    /** Say what happened in the surface's own words, when it knows more than the
     *  change feed does — e.g. a kept edit offered back after the item changed. */
    message?: string;
  },
) {
  const text = message
    ? message
    : deleted
    ? 'This item was deleted while you were editing. Reload to check before saving.'
    : coarse
      ? 'This item may have changed while you were editing. Saving could overwrite other changes.'
      : `This item was just changed${actorName ? ` by ${actorName}` : ' by someone else'}. Saving could overwrite those changes.`;
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          role="status"
          aria-live="polite"
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.18 }}
          style={{
            ...bannerBase,
            background: 'var(--accent-warning-soft)',
            color: 'var(--accent-warning)',
            border: '1px solid var(--accent-warning)',
          }}
        >
          <AlertTriangle size={15} />
          <span style={{ flex: 1 }}>{text}</span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onKeep}>Keep editing</button>
          <button type="button" className="btn btn-primary btn-sm" onClick={onReload}>{reloadLabel}</button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
